/**
 * A press on the game world takes focus from a text field. Phaser cancels every `mousedown` on its canvas,
 * which stops the browser from moving focus there, so without this a field over the world would keep the
 * keyboard after the player clicked back into the game. The field's `focusout` then hands the keyboard to
 * the game at once (`router.ts`). A press on anything but the world, an overlay control included, is left
 * to the browser.
 */

/**
 * How a world press is watched.
 */
export interface WorldPressOptions {
  /**
   * Where presses arrive: the element that holds the game's canvas. It is listened to in the capture phase,
   * so the blur happens before Phaser sees the press.
   */
  readonly host: EventTarget;
  /**
   * The game's canvas, read on each press since the game makes it after the watch starts.
   *
   * @returns The canvas, or null before there is one.
   */
  readonly world: () => unknown;
  /**
   * Whether an element is a text field that takes typing.
   *
   * @param target The element, or null.
   * @returns True for a text field.
   */
  readonly isTextField: (target: unknown) => boolean;
  /**
   * What has focus now.
   *
   * @returns The focused element, or null.
   */
  readonly focused: () => unknown;
}

/**
 * Whether a press is the world's (ADR-061's amendment "a play window never stops play"): only a press whose target
 * is the game's canvas. A press on a window, a HUD control or anything else drawn over the world is that surface's,
 * and no press is the world's before the game has a canvas.
 *
 * @param target What was pressed.
 * @param world The game's canvas, or null before it exists.
 * @returns True when the press landed on the world.
 * @example
 * isWorldPress(event.target, game.canvas);
 */
export function isWorldPress(target: unknown, world: unknown): boolean {
  return world !== null && target === world;
}

/**
 * Watches the world for presses and blurs a focused text field on each one.
 *
 * @param options The host, the canvas reader, the text-field test and the focus reader.
 * @returns A function that stops watching.
 * @example
 * ```ts
 * const stop = releaseFieldOnWorldPress({ host, world: () => game.canvas, isTextField, focused: () => document.activeElement });
 * ```
 */
export function releaseFieldOnWorldPress(options: WorldPressOptions): () => void {
  /**
   * Blurs the focused field when the press landed on the world.
   *
   * @param event The press.
   */
  const onPress = (event: Event): void => {
    if (!isWorldPress(event.target, options.world())) {
      return;
    }
    const focused = options.focused();
    if (options.isTextField(focused)) {
      (focused as { blur(): void }).blur();
    }
  };
  options.host.addEventListener("pointerdown", onPress, { capture: true });
  return () => options.host.removeEventListener("pointerdown", onPress, { capture: true });
}
